/**
 * PoolDriverView — l'espace de l'agent conducteur dans le parc partagé.
 *
 * Le pendant de `PoolExceptionsView` (côté gestionnaire) : ici, celui qui prend
 * le volant. Jusqu'au 2026-09-12 les douze routes `/drivers/me/pool/*` et leur
 * client `api/pool.js` existaient sans qu'AUCUN écran ne les consomme.
 *
 * LA RÈGLE QUI GOUVERNE CET ÉCRAN — il est SITUATIONNEL
 *
 * À chaque instant il montre l'étape où l'agent se trouve et le SEUL geste
 * suivant, jamais un menu de douze options : un agent qui prend un véhicule
 * trois fois par an ne doit rien avoir à apprendre. C'est pourquoi la colonne
 * « Ma situation » ne montre jamais qu'UN de ses quatre états (recherche,
 * réservation en attente, demande en attente d'accord, garde active) — la
 * colonne « Le parc », elle, reste un repère visuel constant, en lecture
 * seule hors recherche (voir `canBook` plus bas).
 *
 * ⚠️ La situation vient du SERVEUR (`GET /drivers/me/pool`), jamais d'un
 * sélecteur d'écran. Il n'existe donc pas de bouton « passer à l'étape 2 » :
 * une maquette a le droit de naviguer entre ses états, un produit non — ce
 * serait fabriquer une détention qui n'existe pas.
 *
 * CE QUI VIENT DU SERVEUR ET QU'ON NE RECALCULE PAS
 *   - la politique (`policy`) : fenêtre d'ouverture anticipée, délai de
 *     non-présentation, marge de remise en état. Servie avec l'écran (ADR-011)
 *     pour qu'un changement de règle ne coûte pas une réinstallation ;
 *   - `server_time` : l'heure de référence. L'horloge du poste peut être fausse,
 *     et c'est le serveur qui tranchera de toute façon ;
 *   - les drapeaux (`flags`) : retard, véhicule à risque, défaut bloquant,
 *     batterie faible, véhicule silencieux ;
 *   - `next_actions` sur un refus métier : un refus qui ne dit que « non »
 *     renvoie l'agent au téléphone.
 *
 * `requested` (en attente d'accord) et `confirmed` (confirmée) ne se
 * confondent JAMAIS : les deux retiennent le véhicule, une seule autorise la
 * prise.
 *
 * FORMAT (retour JGS, test Chrome 2026-09-13) — l'écran occupe la largeur de
 * la zone de contenu, comme `PoolExceptionsView` : même carte d'en-tête
 * blanche, icône + titre + sous-titre, et un layout à deux colonnes en
 * desktop (« Ma situation » / « Le parc »), une seule en mobile. `V1Wrapper`
 * (`frontend/src/v3/App.jsx`, cas `pool_driver`) ne pose qu'un padding —
 * c'était le `mx-auto max-w-2xl` de CE fichier qui coinçait la vue en quart
 * d'écran, pas le montage.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Car, Clock, KeyRound, AlertTriangle, CheckCircle2, RotateCcw, ChevronRight,
  BatteryLow, WifiOff, LayoutGrid, X, CalendarPlus,
  CalendarClock,
} from 'lucide-react';
import poolAPI, { POOL_ERROR_CODES } from '../../api/pool.js';
import { driverSelfAPI } from '../../api/drivers.js';
import { useFormat } from '../../i18n/format.js';
import { isFlagOn } from '../../utils/dbFlags.js';
import SkeletonLoader from '../common/SkeletonLoader.jsx';
import VehiclePickerGrid from './driver/VehiclePickerGrid.jsx';
import ScannedVehicleCard from './driver/ScannedVehicleCard.jsx';
import HandoverForm from './driver/HandoverForm.jsx';
import DriverFuelCard from './driver/DriverFuelCard.jsx';
import PoolBoardingPass from './driver/PoolBoardingPass.jsx';
import PoolBoardingPassNotice from './driver/PoolBoardingPassNotice.jsx';
import DriverFuelPanel from './driver/DriverFuelPanel.jsx';
import DriverActivityPanel from './driver/DriverActivityPanel.jsx';
import DriverProblemPanel from './driver/DriverProblemPanel.jsx';

/** Les statuts qui retiennent encore le véhicule (miroir de BLOCKING_STATUSES côté serveur). */
const LIVE_STATUSES = ['requested', 'confirmed', 'active'];

/** Une durée en minutes, lisible : « 45 min », « 2 h 10 », « 22 h 40 », « 2 j 03 h ». */
function formatDuration(min) {
  const m = Math.abs(Math.round(min ?? 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h < 24) return `${h} h ${String(r).padStart(2, '0')}`;
  const d = Math.floor(h / 24);
  return `${d} j ${String(h % 24).padStart(2, '0')} h`;
}

const minutesBetween = (from, to) => (new Date(to) - new Date(from)) / 60000;

/** Valeur d'un `<input type="datetime-local">` à partir d'une date. */
function toLocalInput(date) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Le bandeau d'alerte d'un drapeau serveur. Rendu seulement si le drapeau est
 * posé : on ne rassure pas non plus, un écran qui dit « tout va bien » à chaque
 * ligne finit par n'être plus lu.
 */
function FlagBanner({ flags, t }) {
  if (!flags) return null;
  const items = [
    flags.vehicle_blocked && { key: 'vehicleBlocked', Icon: AlertTriangle, tone: 'bg-red-50 text-red-800 border-red-200' },
    flags.at_risk && { key: 'atRisk', Icon: Clock, tone: 'bg-amber-50 text-amber-900 border-amber-200' },
    flags.battery_low && { key: 'batteryLow', Icon: BatteryLow, tone: 'bg-amber-50 text-amber-900 border-amber-200' },
    flags.vehicle_silent && { key: 'vehicleSilent', Icon: WifiOff, tone: 'bg-gray-50 text-gray-700 border-gray-200' },
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="space-y-2">
      {items.map(({ key, Icon, tone }) => (
        <div key={key} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${tone}`}>
          <Icon className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{t(`poolDriver.alerts.${key}`)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Un refus métier du module, rendu lisible. `next_actions` vient du serveur :
 * l'écran les DESSINE, il n'en décide aucune.
 */
function RefusalBanner({ error, t, onAction }) {
  if (!error) return null;
  const actions = error.next_actions || error.data?.next_actions || [];
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-900">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="font-medium">{t('poolDriver.refused.title')}</p>
          <p className="mt-0.5">{error.message}</p>
          {actions.length > 0 && (
            <>
              <p className="mt-2 font-medium">{t('poolDriver.refused.whatNow')}</p>
              <ul className="mt-1 space-y-1">
                {actions.map((a, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => onAction?.(a)}
                      className="inline-flex items-center gap-1 text-red-900 underline underline-offset-2 hover:text-red-700"
                    >
                      {a.label || a.code}
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Carte d'identité du véhicule — plaque lisible, c'est ce qu'on cherche sur un
 * parking.
 *
 * Une réservation peut être faite par CLASSE : le véhicule n'est alors pas
 * encore désigné, et il n'y a pas de plaque à montrer. On le dit, au lieu de
 * laisser un vide que l'agent prendrait pour une panne d'affichage.
 */
function VehicleIdentityCard({ vehicle, vehicleClass, t }) {
  if (!vehicle) {
    if (!vehicleClass) return null;
    return (
      <div className="flex items-center gap-3 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
          <Car className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-gray-800">{t('poolDriver.reservation.byClass', { class: vehicleClass })}</p>
          <p className="text-sm text-gray-500">{t('poolDriver.reservation.byClassHint')}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
        <Car className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="truncate font-semibold text-gray-900">
          {[vehicle.make, vehicle.model].filter(Boolean).join(' ') || t('poolDriver.reservation.vehicle')}
        </p>
        <p className="text-sm text-gray-500">{vehicle.license_plate || `#${vehicle.id}`}</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {string|null} [scanToken] - jeton du QR pare-brise scanné (App.jsx, `?scan=`)
 * @param {() => void} [onScanDone] - le conducteur a fermé la carte « véhicule scanné »
 */
function PoolDriverView({ scanToken = null, onScanDone = null } = {}) {
  const { t, fmt } = useFormat();
  const queryClient = useQueryClient();

  const [range, setRange] = useState(() => {
    const start = new Date(Math.ceil(Date.now() / 900000) * 900000);   // prochain quart d'heure
    return { from: toLocalInput(start), to: toLocalInput(new Date(start.getTime() + 3 * 3600_000)) };
  });
  const [handover, setHandover] = useState(null);   // 'checkout' | 'checkin' | null
  const [receipt, setReceipt] = useState(null);
  const [refusal, setRefusal] = useState(null);
  // Volet « Prolonger » : la valeur du champ, ou `null` quand le volet est fermé.
  const [extendUntil, setExtendUntil] = useState(null);

  const home = useQuery({
    queryKey: ['pool-driver-home'],
    queryFn: () => poolAPI.getHome(),
    refetchInterval: 60_000,
  });

  const data = home.data?.data || home.data || {};
  const policy = data.policy || {};
  const serverNow = data.server_time ? new Date(data.server_time) : new Date();
  const custody = data.active_custody || null;
  const reservations = useMemo(
    () => (data.reservations || []).filter((r) => LIVE_STATUSES.includes(r.status)),
    [data.reservations]
  );
  /** La réservation qui compte : celle en cours, sinon la prochaine à venir. */
  const upcoming = useMemo(() => {
    if (custody) return null;
    return [...reservations]
      .filter((r) => r.status === 'confirmed' || r.status === 'requested')
      .sort((a, b) => new Date(a.planned_start) - new Date(b.planned_start))[0] || null;
  }, [reservations, custody]);

  // Le geste proposé par un scan dépend de l'état : après une réservation, une prise ou un
  // retour, la carte « véhicule scanné » doit relire le serveur, pas garder l'ancien geste.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['pool-driver-home'] });
    queryClient.invalidateQueries({ queryKey: ['pool-scan'] });
  };
  const onError = (err) => setRefusal({ message: err?.message || String(err), ...(err?.data || {}) });

  // ── La situation, décidée par le serveur ──────────────────────────────────
  // « manquée » : la réservation est encore `confirmed` mais la grâce est passée —
  // le serveur refuserait la prise (NO_SHOW_EXPIRED) et le balayage no-show ne
  // l'a pas encore expirée. Sans cet état, l'écran promettait une annulation qui
  // n'arrivait pas et gardait un bouton « Prendre » vert qui échouait (2026-09-15).
  const noShowAt = upcoming?.planned_start
    ? new Date(new Date(upcoming.planned_start).getTime() + (policy.no_show_grace_min ?? 30) * 60000)
    : null;
  let situation = 'search';
  if (custody) situation = 'active';
  else if (upcoming?.status === 'confirmed') situation = noShowAt && serverNow >= noShowAt ? 'missed' : 'waiting';
  else if (upcoming?.status === 'requested') situation = 'requested';

  const rangeValid = new Date(range.to) > new Date(range.from);
  /**
   * « Le parc » reste visible quelle que soit la situation — c'est un repère,
   * pas une action. Mais réserver un second véhicule pendant qu'on en détient
   * déjà un (ou qu'on en attend un) romprait la règle « un seul geste
   * suivant » : `canBook` ferme l'action de réservation sur les cartes sans
   * masquer l'état du parc.
   */
  const canBook = situation === 'search';

  const availability = useQuery({
    queryKey: ['pool-driver-availability', range.from, range.to],
    queryFn: () => poolAPI.getAvailability({
      from: new Date(range.from).toISOString(),
      to: new Date(range.to).toISOString(),
    }),
    // Pas de lecture de disponibilité quand le parc n'est pas affiché.
    enabled: rangeValid && canBook,
  });
  const vehicles = availability.data?.data || availability.data || [];

  const book = useMutation({
    mutationFn: (vehicleId) => poolAPI.createReservation({
      vehicle_id: vehicleId,
      planned_start: new Date(range.from).toISOString(),
      planned_end: new Date(range.to).toISOString(),
    }),
    onSuccess: () => { setRefusal(null); invalidate(); },
    onError,
  });
  const cancel = useMutation({
    mutationFn: (id) => poolAPI.cancelReservation(id),
    onSuccess: () => { setRefusal(null); invalidate(); },
    onError,
  });
  /**
   * Le prefill sert les DEUX passations. Le serveur ne borne pas cette lecture
   * à une réservation « à venir » (`getCheckoutPrefill` ne regarde pas le
   * statut) : au retour, la même requête apporte les défauts déjà ouverts et le
   * vocabulaire fermé — sans quoi le conducteur re-signalerait la rayure que
   * tout le monde connaît déjà.
   */
  const handoverReservationId = handover === 'checkin'
    ? custody?.reservation?.id
    : upcoming?.id;
  const prefill = useQuery({
    queryKey: ['pool-checkout-prefill', handoverReservationId],
    queryFn: () => poolAPI.getCheckoutPrefill(handoverReservationId),
    enabled: !!handover && !!handoverReservationId,
  });
  const prefillData = prefill.data?.data || prefill.data || null;

  /**
   * MA carte carburant. Silencieuse par construction : sans carte — ou tant que
   * la façade conducteur n'existe pas côté serveur (cf. `api/drivers.js`) — le
   * bloc ne s'affiche pas du tout. Un seul essai : une route absente ne doit
   * pas être redemandée trois fois par affichage.
   */
  const fuelCards = useQuery({
    queryKey: ['driver-fuel-cards'],
    queryFn: () => driverSelfAPI.getMyFuelCards(),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const myFuelCards = fuelCards.isError ? [] : (fuelCards.data?.data || fuelCards.data || []);

  // Ma fiche, pour l'U.T. et ses coordonnées : le repli des prix carburant quand
  // le navigateur ne donne pas la position. Erreur silencieuse : sans site, pas de prix.
  const me = useQuery({ queryKey: ['driver-me'], queryFn: () => driverSelfAPI.getMe(), staleTime: 10 * 60_000, retry: false });
  const mySite = me.data?.ok ? me.data.data?.site : null;
  const siteCoords = mySite?.gps_latitude != null && mySite?.gps_longitude != null
    ? { lat: Number(mySite.gps_latitude), lon: Number(mySite.gps_longitude) }
    : null;

  /**
   * PROLONGER — le serveur tranche. Sur refus il peut dire jusqu'où il aurait
   * dit oui (`max_end`) : c'est cette valeur qu'on propose en un clic, plutôt
   * que de laisser le conducteur tâtonner de quart d'heure en quart d'heure.
   */
  const extend = useMutation({
    mutationFn: (iso) => poolAPI.extendReservation(custody.reservation.id, iso),
    onSuccess: () => { setExtendUntil(null); setRefusal(null); invalidate(); },
    onError,
  });
  const extendMaxEnd = refusal?.max_end || null;
  const checkout = useMutation({
    mutationFn: (payload) => poolAPI.checkout(upcoming.id, { ...payload, idempotency_key: `co-${upcoming.id}-${Date.now()}` }),
    onSuccess: () => { setHandover(null); setRefusal(null); invalidate(); },
    onError,
  });
  const checkin = useMutation({
    mutationFn: (payload) => poolAPI.checkin(custody.reservation.id, { ...payload, idempotency_key: `ci-${custody.reservation.id}-${Date.now()}` }),
    onSuccess: (res) => {
      const d = res?.data || res || {};
      // Le reçu est fondé sur la RÉPONSE du serveur, jamais sur ce qu'on a
      // envoyé : un retour en échec ne doit jamais s'afficher comme réussi.
      setReceipt({
        time: d.handover?.received_at || d.handover?.captured_at || new Date().toISOString(),
        odometer: d.handover?.odometer_km,
        fuel: d.handover?.fuel_level_pct,
        // Trois états, pas deux : aucun défaut, un défaut, ou le serveur ne
        // s'est pas prononcé. Un `!== false` mettrait « aucun défaut signalé »
        // sur un `0` (SQLite, PG entier) — et ce reçu est ce que l'agent garde
        // comme preuve de l'état dans lequel il a rendu le véhicule.
        noDefect: d.handover?.no_new_defect == null
          ? null
          : isFlagOn(d.handover.no_new_defect),
        distance: d.distance_km,
        // « Réserver à nouveau » : le même créneau, décalé d'un jour — le
        // geste Uber, le retour vers l'appli est immédiat (JGS 14/09).
        rebook: custody?.reservation?.planned_start && custody?.reservation?.planned_end
          ? { from: custody.reservation.planned_start, to: custody.reservation.planned_end }
          : null,
      });
      setHandover(null);
      setRefusal(null);
      invalidate();
    },
    onError,
  });

  // ── États d'attente et d'erreur ───────────────────────────────────────────
  if (home.isLoading) {
    return (
      <div className="p-4">
        <SkeletonLoader type="card" />
      </div>
    );
  }
  if (home.isError) {
    const notADriver = /NOT_A_DRIVER|driver/i.test(home.error?.message || '');
    return (
      <div className="p-4">
        <div className="mx-auto max-w-lg rounded-xl border border-gray-200 bg-white p-6 text-center">
          <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-amber-500" />
          <h2 className="text-lg font-semibold text-gray-900">
            {t(notADriver ? 'poolDriver.notADriver.title' : 'poolDriver.error.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {notADriver ? t('poolDriver.notADriver.body') : home.error?.message}
          </p>
          {!notADriver && (
            <button
              onClick={() => home.refetch()}
              className="mt-4 min-h-[44px] rounded-lg bg-emerald-600 px-4 py-2.5 font-medium text-white hover:bg-emerald-700"
            >
              {t('poolDriver.error.retry')}
            </button>
          )}
        </div>
      </div>
    );
  }

  const openFrom = upcoming?.planned_start
    ? new Date(new Date(upcoming.planned_start).getTime() - (policy.open_early_min ?? 15) * 60000)
    : null;
  const canTake = situation === 'waiting' && openFrom && serverNow >= openFrom;

  return (
    <div className="p-4 sm:p-6">
      {/* En-tête — même format que PoolExceptionsView : carte blanche,
          icône + titre + sous-titre, pleine largeur. */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="rounded-lg bg-emerald-100 p-3">
            <KeyRound className="h-8 w-8 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('poolDriver.title')}</h1>
            <p className="text-sm text-gray-500">{t('poolDriver.subtitle')}</p>
            <p className="mt-1 text-sm font-medium text-emerald-700">{t(`poolDriver.situation.${situation}`)}</p>
          </div>
        </div>
      </div>

      {receipt && (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <div className="text-sm text-emerald-900">
              <p className="font-medium">
                {t('poolDriver.handover.receipt', {
                  time: fmt.time(receipt.time),
                  odometer: receipt.odometer != null ? fmt.number(receipt.odometer) : '—',
                  fuel: receipt.fuel != null ? receipt.fuel : '—',
                  // `null` = le serveur ne s'est pas prononcé : on n'affirme
                  // alors NI l'un NI l'autre. Le reçu est une preuve, pas une
                  // phrase à compléter.
                  defect: receipt.noDefect == null
                    ? ''
                    : t(receipt.noDefect ? 'poolDriver.handover.receiptNoDefect' : 'poolDriver.handover.receiptWithDefect'),
                })}
              </p>
              {receipt.distance != null && (
                <p className="mt-0.5">{t('poolDriver.handover.distance', { count: receipt.distance })}</p>
              )}
              {receipt.rebook && (
                <button
                  type="button"
                  onClick={() => {
                    const day = 24 * 3600_000;
                    setRange({
                      from: toLocalInput(new Date(new Date(receipt.rebook.from).getTime() + day)),
                      to: toLocalInput(new Date(new Date(receipt.rebook.to).getTime() + day)),
                    });
                    setReceipt(null);
                  }}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
                  data-testid="rebook"
                >
                  <CalendarPlus className="h-4 w-4" />
                  {t('poolDriver.rebook.sameSlotTomorrow')}
                </button>
              )}
            </div>
            <button onClick={() => setReceipt(null)} className="ml-auto text-emerald-700" aria-label={t('poolDriver.handover.cancel')}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {!handover && <div className="mb-6"><RefusalBanner error={refusal} t={t} /></div>}

      {/* QR pare-brise : le véhicule scanné et le geste que le serveur lui associe. Un seul
          véhicule vivant par conducteur (règle DRIVER_BUSY) : la réservation proposée par le
          scan est donc celle que « Prendre » et « Rendre » visent déjà. */}
      {scanToken && !handover && (
        <div className="mb-6">
          <ScannedVehicleCard
            token={scanToken}
            canBook={canBook && rangeValid}
            canCheckout={Boolean(canTake)}
            canReturn={situation === 'active'}
            booking={book.isPending}
            onBook={(vehicleId) => book.mutate(vehicleId)}
            onCheckout={() => setHandover('checkout')}
            onReturn={() => setHandover('checkin')}
            onDismiss={() => onScanDone?.()}
            t={t}
            fmt={fmt}
          />
        </div>
      )}

      {/* Deux colonnes en desktop (« Ma situation » / « Le parc »), une en
          mobile — jamais de conteneur qui coince la vue en quart d'écran. */}
      {/* Le parc n'est là QUE quand on peut réserver (retour JGS 14/09, capture :
          « la vue devrait cacher les véhicules du pool ») : avec une réservation
          ou une garde, montrer 15 véhicules « indisponibles » sous un « terminez
          d'abord » n'est qu'un mur de cartes. La situation prend alors la place. */}
      {/* Deux colonnes ÉGALES : la carte d'embarquement (QR, dates) a besoin de
          largeur autant que les trois panneaux de droite (capture JGS 14/09). */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
        {/* ── Colonne « Ma situation » ─────────────────────────────────── */}
        <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="text-base font-semibold text-gray-900">{t('poolDriver.layout.myStatus')}</h2>

          {/* Je détiens un véhicule */}
          {situation === 'active' && custody && (
            <div className="space-y-3">
              <VehicleIdentityCard vehicle={custody.vehicle} t={t} />
              <FlagBanner flags={custody.reservation?.flags} t={t} />

              {custody.reservation?.flags?.late ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                  <p className="text-lg font-bold text-red-700">
                    {t('poolDriver.custody.late', { duration: formatDuration(custody.reservation.flags.late_minutes) })}
                  </p>
                  <p className="mt-0.5 text-sm text-red-900">{t('poolDriver.custody.lateHint')}</p>
                </div>
              ) : (
                <div className="rounded-lg bg-gray-50 p-3 text-center">
                  <p className="text-xs uppercase tracking-wide text-gray-500">{t('poolDriver.custody.remaining')}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatDuration(minutesBetween(serverNow, custody.reservation.planned_end))}
                  </p>
                  <p className="mt-0.5 text-sm text-gray-600">
                    {t('poolDriver.custody.dueAt', { time: fmt.time(custody.reservation.planned_end) })}
                  </p>
                </div>
              )}

              {handover === 'checkin' ? (
                <HandoverForm
                  mode="checkin"
                  prefill={{ ...(prefillData || {}), odometer: prefillData?.odometer || custody.odometer }}
                  previousOdometerKm={custody.odometer?.km ?? null}
                  pending={checkin.isPending}
                  RefusalBanner={<RefusalBanner error={refusal} t={t} />}
                  onSubmit={(p) => checkin.mutate(p)}
                  onCancel={() => { setHandover(null); setRefusal(null); }}
                  t={t}
                  fmt={fmt}
                  formatDuration={formatDuration}
                />
              ) : (
                <>
                  <button
                    onClick={() => { setRefusal(null); setHandover('checkin'); }}
                    className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 text-base font-semibold text-white hover:bg-emerald-700"
                  >
                    <RotateCcw className="h-5 w-5" />
                    {t('poolDriver.custody.return')}
                  </button>

                  {extendUntil === null ? (
                    <button
                      onClick={() => {
                        setRefusal(null);
                        // Une heure de plus par défaut : la demande la plus
                        // fréquente, et elle reste modifiable.
                        setExtendUntil(toLocalInput(new Date(new Date(custody.reservation.planned_end).getTime() + 3600_000)));
                      }}
                      className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <CalendarPlus className="h-4 w-4" />
                      {t('poolDriver.reservation.extend')}
                    </button>
                  ) : (
                    <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolDriver.reservation.extendPrompt')}</span>
                        <input
                          type="datetime-local"
                          value={extendUntil}
                          onChange={(e) => setExtendUntil(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </label>

                      {/* Le serveur a dit jusqu'où il aurait dit oui : on le
                          propose en un geste, au lieu de laisser tâtonner. */}
                      {extendMaxEnd && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                          <p className="text-sm text-amber-900">
                            {t('poolDriver.reservation.extendMaxEnd', { time: fmt.time(extendMaxEnd) })}
                          </p>
                          <button
                            onClick={() => { setExtendUntil(toLocalInput(extendMaxEnd)); extend.mutate(extendMaxEnd); }}
                            disabled={extend.isPending}
                            className="mt-1.5 min-h-[40px] rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                          >
                            {t('poolDriver.reservation.extendUseMax', { time: fmt.time(extendMaxEnd) })}
                          </button>
                        </div>
                      )}

                      <div className="flex flex-col gap-2 sm:flex-row-reverse">
                        <button
                          onClick={() => extend.mutate(new Date(extendUntil).toISOString())}
                          disabled={extend.isPending || !extendUntil || new Date(extendUntil) <= new Date(custody.reservation.planned_end)}
                          className="min-h-[44px] flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
                        >
                          {extend.isPending ? t('poolDriver.reservation.extending') : t('poolDriver.reservation.extend')}
                        </button>
                        <button
                          onClick={() => { setExtendUntil(null); setRefusal(null); }}
                          disabled={extend.isPending}
                          className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        >
                          {t('poolDriver.handover.cancel')}
                        </button>
                      </div>

                      {/* Un bouton désactivé dit POURQUOI. */}
                      {extendUntil && new Date(extendUntil) <= new Date(custody.reservation.planned_end) && (
                        <p className="text-xs text-gray-600">
                          {t('poolDriver.reservation.extendMustBeLater', { time: fmt.time(custody.reservation.planned_end) })}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* J'ai une réservation (confirmée ou en attente d'accord) */}
          {(situation === 'waiting' || situation === 'requested') && upcoming && (
            <div className="space-y-3">
              {/* « Votre véhicule est réservé » : la notification du guichet, tant
                  qu'elle n'est pas lue (carte d'embarquement, 2026-09-14). */}
              <PoolBoardingPassNotice />
              {situation === 'requested' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <p className="font-medium">{t('poolDriver.reservation.awaitingApproval')}</p>
                  <p className="mt-0.5">{t('poolDriver.reservation.awaitingApprovalHint')}</p>
                </div>
              )}

              {situation === 'waiting' && (
                <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-900">
                  {minutesBetween(serverNow, upcoming.planned_start) >= 0
                    ? t('poolDriver.reservation.startsIn', { duration: formatDuration(minutesBetween(serverNow, upcoming.planned_start)) })
                    : t('poolDriver.reservation.startedAgo', { duration: formatDuration(minutesBetween(upcoming.planned_start, serverNow)) })}
                </p>
              )}

              <VehicleIdentityCard vehicle={upcoming.vehicle} vehicleClass={upcoming.vehicle_class} t={t} />
              <FlagBanner flags={upcoming.flags} t={t} />

              {/* La carte d'embarquement (2026-09-14) : le véhicule, où il est garé,
                  le créneau élargi (politique serveur), la destination, les défauts
                  connus, la carte carburant, le QR de prise. Elle remplace l'ancien
                  récapitulatif créneau / site / fenêtre de prise, qu'elle contient. */}
              <PoolBoardingPass reservationId={upcoming.id} />

              {handover === 'checkout' ? (
                <HandoverForm
                  mode="checkout"
                  prefill={prefillData}
                  pending={checkout.isPending}
                  RefusalBanner={<RefusalBanner error={refusal} t={t} />}
                  onSubmit={(p) => checkout.mutate(p)}
                  onCancel={() => { setHandover(null); setRefusal(null); }}
                  t={t}
                  fmt={fmt}
                  formatDuration={formatDuration}
                />
              ) : (
                <>
                  <button
                    onClick={() => { setRefusal(null); setHandover('checkout'); }}
                    disabled={!canTake}
                    className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 text-base font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
                  >
                    <KeyRound className="h-5 w-5" />
                    {t('poolDriver.reservation.take')}
                  </button>
                  {!canTake && openFrom && situation === 'waiting' && (
                    <p className="text-center text-xs text-gray-500">
                      {t('poolDriver.reservation.takeNotYet', {
                        time: fmt.time(openFrom),
                        minutes: policy.open_early_min ?? 15,
                      })}
                    </p>
                  )}
                  {noShowAt && situation === 'waiting' && (
                    <p className="border-l-2 border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      {t('poolDriver.reservation.noShowWarning', { time: fmt.time(noShowAt) })}
                    </p>
                  )}
                  <button
                    onClick={() => {
                      // eslint-disable-next-line no-alert
                      if (window.confirm(t('poolDriver.reservation.cancelConfirm'))) cancel.mutate(upcoming.id);
                    }}
                    disabled={cancel.isPending}
                    className="min-h-[44px] w-full rounded-lg px-4 py-2.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                  >
                    {cancel.isPending ? t('poolDriver.reservation.cancelling') : t('poolDriver.reservation.cancel')}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Créneau manqué : le véhicule est rendu au parc, on repart d'une
              nouvelle réservation. « Réserver à nouveau » annule l'ancienne (le
              serveur l'accepte tant qu'elle n'est pas prise) et rouvre le parc. */}
          {situation === 'missed' && upcoming && (
            <div className="space-y-3">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <p className="font-medium">{t('poolDriver.reservation.missedTitle')}</p>
                <p className="mt-0.5">
                  {t('poolDriver.reservation.missedHint', {
                    time: fmt.time(noShowAt),
                    minutes: policy.no_show_grace_min ?? 30,
                    start: fmt.time(new Date(upcoming.planned_start)),
                  })}
                </p>
              </div>
              <VehicleIdentityCard vehicle={upcoming.vehicle} vehicleClass={upcoming.vehicle_class} t={t} />
              <button
                onClick={() => cancel.mutate(upcoming.id)}
                disabled={cancel.isPending}
                className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 text-base font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                <CalendarClock className="h-5 w-5" />
                {cancel.isPending ? t('poolDriver.reservation.cancelling') : t('poolDriver.reservation.rebook')}
              </button>
              <p className="text-center text-xs text-gray-500">{t('poolDriver.reservation.missedContact')}</p>
            </div>
          )}

          {/* Je cherche */}
          {situation === 'search' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">{t('poolDriver.search.need')}</p>
              <div className="grid gap-3">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolDriver.search.from')}</span>
                  <input
                    type="datetime-local"
                    value={range.from}
                    onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolDriver.search.to')}</span>
                  <input
                    type="datetime-local"
                    value={range.to}
                    onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </label>
              </div>
              {!rangeValid && (
                <p className="text-sm text-red-700">{t('poolDriver.search.invalidRange')}</p>
              )}
              <p className="text-xs text-gray-500">{t('poolDriver.layout.fleetHint')}</p>
            </div>
          )}

          {/* Ma carte carburant — sous la situation, quelle qu'elle soit : la
              carte sert à la pompe, pas seulement pendant une garde. Rien à
              l'écran si le conducteur n'en a aucune. */}
          {canBook && <DriverFuelCard cards={myFuelCards} t={t} fmt={fmt} />}
        </section>

        {/* ── Colonne de droite : le parc quand on peut réserver, sinon les
            trois panneaux repris de l'APK et élagués (JGS 14/09 : « il en
            faut un peu sinon on va se dire que cela ne sert à rien ») :
            Carburant · Mes déplacements · Un problème ? ───────────────── */}
        {!canBook && (
          <div className="space-y-6">
            <DriverFuelPanel
              situation={situation}
              vehicle={custody?.vehicle || upcoming?.vehicle || null}
              fuelCards={myFuelCards}
              fallbackCoords={siteCoords}
            />
            <DriverActivityPanel reservations={data.reservations || []} custody={custody} />
            <DriverProblemPanel custody={custody} />
          </div>
        )}
        {canBook && (
        <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <LayoutGrid className="h-4 w-4 text-gray-400" />
            {t('poolDriver.layout.fleet')}
          </h2>
          {!canBook && (
            <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
              {t('poolDriver.picker.bookDisabledHint')}
            </p>
          )}
          <VehiclePickerGrid
            vehicles={vehicles}
            loading={availability.isLoading || availability.isFetching}
            canBook={canBook}
            booking={book.isPending}
            onChoose={(vehicleId) => book.mutate(vehicleId)}
            t={t}
          />
        </section>
        )}
      </div>
    </div>
  );
}

export default PoolDriverView;
export { formatDuration, POOL_ERROR_CODES };
